import { PhoneIncoming, PhoneOutgoing, Smartphone, Radio } from 'lucide-react'

import type { Call } from '@/types/api/calling'
import { cn } from '@/utils/cn'

import { formatDuration, outcomeLabel, outcomeTone, PROVIDER_BADGE } from '../callText'

const chip = 'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium whitespace-nowrap'

export function OutcomeBadge({ call }: { call: Call }) {
  return (
    <span className={cn(chip, outcomeTone(call.status === 'ENDED' ? call.outcome : null))}>
      {call.status === 'ENDED' ? outcomeLabel(call.outcome, call.direction) : 'In progress'}
      {call.outcome === 'ANSWERED' && call.connectedDurationSec !== null && (
        <span className="tabular-nums opacity-80">· {formatDuration(call.connectedDurationSec)}</span>
      )}
    </span>
  )
}

/** How the call's state was learned: carrier webhooks or the phone's call log. */
export function TrackingBadge({ call }: { call: Call }) {
  const sim = call.trackingSource === 'DEVICE_LOG'
  return (
    <span
      className={cn(chip, sim ? 'border-violet-200 bg-violet-50 text-violet-700' : 'border-sky-200 bg-sky-50 text-sky-700')}
      title={sim ? 'Placed on the agent\'s SIM; result read from the phone\'s call log' : 'Bridged by Exotel; status reported by the carrier'}
    >
      {sim ? <Smartphone className="h-3 w-3" /> : <Radio className="h-3 w-3" />}
      {PROVIDER_BADGE[call.trackingSource]}
    </span>
  )
}

export function DirectionIcon({ call }: { call: Call }) {
  return call.direction === 'INBOUND'
    ? <PhoneIncoming className="h-4 w-4 text-indigo-500" aria-label="Inbound" />
    : <PhoneOutgoing className="h-4 w-4 text-lime-600" aria-label="Outbound" />
}

export function DispositionChip({ call }: { call: Call }) {
  if (!call.dispositionLabel) return null
  return (
    <span className={cn(chip, 'border-neutral-200 bg-white text-neutral-700')} title={call.dispositionNote ?? undefined}>
      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: call.dispositionColorHex ?? '#94a3b8' }} />
      {call.dispositionLabel}
    </span>
  )
}

export function RecordingPlayer({ call }: { call: Call }) {
  if (!call.recordingUrl) return null
  return (
    <audio controls preload="none" src={call.recordingUrl} className="h-8 w-full max-w-xs">
      <a href={call.recordingUrl}>Recording</a>
    </audio>
  )
}
